import type { DynamoDbHomeDetails } from '@ourbranch/schema-types/dynamodb';

import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

import { baseBindState, getPrimarySegment } from './lib/helpers';

export const confirmNotAForeclosure = defineInternalTask({
  taskName: internalTasks.ConfirmNotAForeclosure,
  config: {
    staffDisplayName: 'Potential foreclosure: verification needed',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['H'],
    enabled: true,
    trigger: ({ policy }) => {
      const segment = getPrimarySegment(policy);
      // PolicyDetails.home is typed as the AppSync HomeDetails (no DDB-only
      // fields). foreclosureHit / foreclosureResponse are written by
      // process-corelogic and live on DynamoDbHomeDetails — narrow with a
      // local cast rather than widening the schema-types contract.
      const home = segment?.home as DynamoDbHomeDetails | undefined;
      if (!home?.foreclosureHit) {
        return null;
      }
      return {
        data: { foreclosureResponse: home?.foreclosureResponse || {} },
        bindState: baseBindState(policy)
      };
    },
    description: ({ data, bindState }) => {
      const entries = Object.entries(data.foreclosureResponse || {});
      // CoreLogic's response is a nested report (see core-logic.ts → CdRmResult),
      // so values may be objects. Pretty-print object values via JSON.stringify
      // instead of letting template-literal coercion emit "[object Object]".
      // When the hit fires without details, render an explicit fallback rather
      // than leaving the section header followed by blank content.
      const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) {
          return 'N/A';
        }
        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2);
        }
        return String(value);
      };
      const responseSection =
        entries.length > 0
          ? `Response from CoreLogic:\n${entries.map(([key, value]) => `  ${key}: ${formatValue(value)}`).join('\n')}`
          : 'Response from CoreLogic: (no response data available)';
      return `${responseSection}\n\nPolicy state: ${bindState.state}`;
    }
  }
});
